import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { CompanyFiltersType, DateRangeValue, FilterState } from '../Types/GlobalTypes';
import { GetFollowPeopleDistincts, GetSearchContentDistincts, GetSimilarPeopleDistincts } from '../Types/RequestBodies';
import { SearchDistinctsData } from '../Types/ResponseBodies';
import { GetAdvancedSearchDistincts } from '../Types/AdvanceSearchResultsTypes';

export const initialState: FilterState = {
  companyFilters: {},
  articleFilters: {
    type: undefined,
    startDate: '',
    endDate: '',
    allTime: false
  },
  companyLoading: false,
  designationLoading: false,
  distinctsFilter: {
    companies: [],
    companiesCount: 0,
    companyPage: 0,
    designations: [],
    designationsCount: 0,
    designationPage: 0,
    page: 0,
  },
  companySearchTerm: '',
  designationSearchTerm: '',
  advDistinctsFilter: {
    companies: [],
    companiesCount: 0,
    companyPage: 0,
    designations: [],
    designationsCount: 0,
    designationPage: 0,
    page: 0,
  },
  advCompanySearchTerm: '',
  advDesignationSearchTerm: '',
  advCompanyLoading: false,
  advDesignationLoading: false,
  similarDistinctsFilter: {
    companies: [],
    companiesCount: 0,
    companyPage: 0,
    designations: [],
    designationsCount: 0,
    designationPage: 0,
    page: 0,
  },
  similarCompanySearchTerm: '',
  similarCompanyLoading: false,
  similarDesignationLoading: false,
  similarDesignationSearchTerm: '',
  followDistinctsFilter: {
    companies: [],
    companiesCount: 0,
    companyPage: 0,
    designations: [],
    designationsCount: 0,
    designationPage: 0,
    page: 0,
  },
  followCompanySearchTerm: '',
  followCompanyLoading: false,
  followDesignationLoading: false,
  followDesignationSearchTerm: '',
};

const FilterSlice = createSlice({
  name: 'filter',
  initialState,
  reducers: {
    getSearchContentDistincts(state, action: PayloadAction<GetSearchContentDistincts>) {
      const { type, page } = action.payload;
      if (type === 'companies') {
        state.companyLoading = true;
        if (page === 1) {
          state.distinctsFilter.companies = [];
          state.distinctsFilter.companiesCount = 0;
        }
      }
      if (type === 'designations') {
        state.designationLoading = true;
        if (page === 1) {
          state.distinctsFilter.designations = [];
          state.distinctsFilter.designationsCount = 0;
        }
      }
    },
    setSearchContentDistincts(state, action: PayloadAction<{ type?: string, response: SearchDistinctsData }>) {
      const { type, response } = action.payload;
      if (type === 'companies') {
        state.distinctsFilter.companies = [
          ...state.distinctsFilter.companies,
          ...(response.companies ?? []),
        ];
        state.distinctsFilter.companiesCount = response.companiesCount;
        state.distinctsFilter.companyPage = response.companyPage;
        state.companyLoading = false;
      }
      if (type === 'designations') {
        state.distinctsFilter.designations = [
          ...state.distinctsFilter.designations,
          ...(response.designations ?? []),
        ];
        state.distinctsFilter.designationsCount = response.designationsCount;
        state.distinctsFilter.designationPage = response.designationPage;
        state.designationLoading = false;
      }
    },
    setCompanySearchTerm(state, action: PayloadAction<string>) {
      state.companySearchTerm = action.payload;
    },
    setDesignationSearchTerm(state, action: PayloadAction<string>) {
      state.designationSearchTerm = action.payload;
    },
    setAdvCompanySearchTerm(state, action: PayloadAction<string>) {
      state.advCompanySearchTerm = action.payload;
    },
    setAdvDesignationSearchTerm(state, action: PayloadAction<string>) {
      state.advDesignationSearchTerm = action.payload;
    },
    setCompanyFilters(state, action: PayloadAction<CompanyFiltersType>) {
      state.companyFilters = action.payload;
    },
    setArticleFilters(state, action: PayloadAction<DateRangeValue>) {
      state.articleFilters = action.payload;
    },
    getAdvancedSearchDistincts(state, action: PayloadAction<GetAdvancedSearchDistincts>) {
      const { type, page } = action.payload;
      if (type === 'companies') {
        state.advCompanyLoading = true;
        if (page === 1) {
          state.advDistinctsFilter.companies = [];
          state.advDistinctsFilter.companiesCount = 0;
        }
      }
      if (type === 'designations') {
        state.advDesignationLoading = true;
        if (page === 1) {
          state.advDistinctsFilter.designations = [];
          state.advDistinctsFilter.designationsCount = 0;
        }
      }
    },
    setAdvancedSearchDistincts(state, action: PayloadAction<{ type?: string, response: SearchDistinctsData }>) {
      const { type, response } = action.payload;
      if (type === 'companies') {
        state.advDistinctsFilter.companies = [
          ...state.advDistinctsFilter.companies,
          ...(response.companies ?? []),
        ];
        state.advDistinctsFilter.companiesCount = response.companiesCount;
        state.advDistinctsFilter.companyPage = response.companyPage;
        state.advCompanyLoading = false;
      }
      if (type === 'designations') {
        state.advDistinctsFilter.designations = [
          ...state.advDistinctsFilter.designations,
          ...(response.designations ?? []),
        ];
        state.advDistinctsFilter.designationsCount = response.designationsCount;
        state.advDistinctsFilter.designationPage = response.designationPage;
        state.advDesignationLoading = false;
      }
    },
    getSimilarPeopleDistincts(state, action: PayloadAction<GetSimilarPeopleDistincts>) {
      const { type, page } = action.payload;
      if (type === 'companies') {
        state.similarCompanyLoading = true;
        if (page === 1) {
          state.similarDistinctsFilter.companies = [];
          state.similarDistinctsFilter.companiesCount = 0;
        }
      }
      if (type === 'designations') {
        state.similarDesignationLoading = true;
        if (page === 1) {
          state.similarDistinctsFilter.designations = [];
          state.similarDistinctsFilter.designationsCount = 0;
        }
      }
    },
    setSimilarDistincts(state, action: PayloadAction<{ type?: string, response: SearchDistinctsData }>) {
      const { type, response } = action.payload;
      if (type === 'companies') {
        state.similarDistinctsFilter.companies = [
          ...state.similarDistinctsFilter.companies,
          ...(response.companies ?? []),
        ];
        state.similarDistinctsFilter.companiesCount = response.companiesCount;
        state.similarDistinctsFilter.companyPage = response.page;
        state.similarCompanyLoading = false;
      }
      if (type === 'designations') {
        state.similarDistinctsFilter.designations = [
          ...state.similarDistinctsFilter.designations,
          ...(response.designations ?? []),
        ];
        state.similarDistinctsFilter.designationsCount = response.designationsCount;
        state.similarDistinctsFilter.designationPage = response.page;
        state.similarDesignationLoading = false;
      }
    },
    setSimilarCompanySearchTerm(state, action: PayloadAction<string>) {
      state.similarCompanySearchTerm = action.payload;
    },
    setSimialrDesignationSearchTerm(state, action: PayloadAction<string>) {
      state.similarDesignationSearchTerm = action.payload;
    },
    getFollowedPeopleDistincts(state, action: PayloadAction<GetFollowPeopleDistincts>) {
      const { type, page } = action.payload;
      if (type === 'companies') {
        state.followCompanyLoading = true;
        if (page === 1) {
          state.followDistinctsFilter.companies = [];
          state.followDistinctsFilter.companiesCount = 0;
        }
      }
      if (type === 'designations') {
        state.followDesignationLoading = true;
        if (page === 1) {
          state.followDistinctsFilter.designations = [];
          state.followDistinctsFilter.designationsCount = 0;
        }
      }
    },
    setFollowedPeopleDistincts(state, action: PayloadAction<{ type: string, response: SearchDistinctsData }>) {
      const { type, response } = action.payload;
      if (type === 'companies') {
        state.followDistinctsFilter.companies = [
          ...state.followDistinctsFilter.companies,
          ...(response.companies ?? []),
        ];
        state.followDistinctsFilter.companiesCount = response.companiesCount;
        state.followDistinctsFilter.companyPage = response.page;
        state.followCompanyLoading = false;
      }
      if (type === 'designations') {
        state.followDistinctsFilter.designations = [
          ...state.followDistinctsFilter.designations,
          ...(response.designations ?? []),
        ];
        state.followDistinctsFilter.designationsCount = response.designationsCount;
        state.followDistinctsFilter.designationPage = response.page;
        state.followDesignationLoading = false;
      }
    },
    setFollowCompanySearchTerm(state, action: PayloadAction<string>) {
      state.followCompanySearchTerm = action.payload;
    },
    setFollowDesignationSearchTerm(state, action: PayloadAction<string>) {
      state.followDesignationSearchTerm = action.payload;
    }
  }
})

export const { actions, reducer, name: sliceKey } = FilterSlice